import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Ajv2020 from 'ajv/dist/2020.js';
import Editor from '@monaco-editor/react';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
/* eslint-disable react/no-unstable-nested-components */
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Input from './shared/Input';
import Select from './shared/Select';
import Textarea from './shared/Textarea';
import Toggle from './shared/Toggle';
import Spinner from './shared/Spinner';
import Alert from './shared/Alert';
import Modal from './shared/Modal';
import TaskOutputsEditor from './TaskOutputsEditor';

const SCHEDULE_TYPES = [
    { value: 'trigger', label: 'Trigger (Manual only)' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'cron', label: 'Cron Expression' },
];

const TIMEZONES = [
    { value: 'Asia/Ho_Chi_Minh', label: 'Asia/Ho_Chi_Minh (UTC+7)' },
    { value: 'Asia/Bangkok', label: 'Asia/Bangkok (UTC+7)' },
    { value: 'Asia/Singapore', label: 'Asia/Singapore (UTC+8)' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC+9)' },
    { value: 'Asia/Shanghai', label: 'Asia/Shanghai (UTC+8)' },
    { value: 'UTC', label: 'UTC' },
    { value: 'US/Eastern', label: 'US/Eastern (UTC-5)' },
    { value: 'US/Pacific', label: 'US/Pacific (UTC-8)' },
    { value: 'Europe/London', label: 'Europe/London (UTC+0)' },
];

const DAYS_OF_WEEK = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
];

const DEFAULT_FORM = {
    id: '',
    name: '',
    description: '',
    agent_id: 'simplize',
    input_message: '',
    system_prompt_override: '',
    schedule_type: 'daily',
    schedule_time: '07:00',
    cron_expression: '',
    day_of_week: 1,
    day_of_month: 1,
    timezone: 'Asia/Ho_Chi_Minh',
    target_user_id: '',
    outputs: [],
    default_variables_json: '{}',
    execution_config: {},
    session_mode: 'isolated',
    timeout_seconds: 300,
    max_retries: 1,
    retry_delay_seconds: 30,
    structured_output_enabled: false,
    output_schema_json: '',
    attach_full_session: true,
    is_active: true,
};

const DEFAULT_SCHEMA_EXAMPLE = `{
  "type": "object",
  "properties": {
    "title": {
      "type": "string",
      "description": "Tiêu đề ngắn gọn",
      "maxLength": 200
    },
    "summary": {
      "type": "string",
      "description": "Tóm tắt 2-3 câu"
    },
    "content": {
      "type": "string",
      "description": "Nội dung chi tiết (markdown)"
    },
    "sentiment": {
      "type": "string",
      "enum": ["bullish", "neutral", "bearish"],
      "description": "Xu hướng thị trường"
    },
    "score": {
      "type": "number",
      "minimum": 0,
      "maximum": 10,
      "description": "Điểm tín hiệu 0-10"
    },
    "tags": {
      "type": "array",
      "items": {"type": "string"},
      "description": "Optional tags"
    }
  },
  "required": ["title", "summary", "content"]
}`;

const INSIGHT_ANALYSIS_SCHEMA = JSON.stringify({
    type: 'object',
    required: [
        'ticker',
        'canonical_id',
        'event_type',
        'severity',
        'hook',
        'summary',
        'content',
        'title',
        'cta',
    ],
    properties: {
        cta: {
            type: 'object',
            required: ['cta_items'],
            properties: {
                cta_intro: {
                    type: 'string',
                    description: 'Câu dẫn vào danh sách follow-up',
                },
                cta_items: {
                    type: 'array',
                    items: {
                        type: 'object',
                        required: ['label', 'prompt'],
                        properties: {
                            label: { type: 'string' },
                            prompt: { type: 'string' },
                            description: { type: 'string' },
                        },
                    },
                },
            },
        },
        hook: {
            type: 'string',
            maxLength: 150,
            description: '1–2 câu ngắn dạng tin nhắn — nêu sự kiện trigger và benefit cụ thể cho người đọc. Dùng cho thông báo đẩy.',
        },
        title: {
            type: 'string',
            maxLength: 200,
            description: 'Tiêu đề ngắn ≤200 ký tự, dùng làm card headline + push title.',
        },
        ticker: { type: 'string' },
        content: { type: 'string' },
        summary: { type: 'string' },
        tickers: {
            type: 'array',
            items: { type: 'string' },
        },
        severity: {
            enum: ['critical', 'important', 'informational'],
            type: 'string',
        },
        event_type: { type: 'string' },
        canonical_id: { type: 'string' },
    },
}, null, 2);

const INSTRUMENT_PROFILE_SCHEMA = JSON.stringify({
    title: 'InstrumentProfile',
    type: 'object',
    required: ['ticker', 'canonical_id', 'title', 'summary', 'content'],
    properties: {
        ticker: { type: 'string', description: 'Mã cổ phiếu' },
        canonical_id: { type: 'string', description: "ID chuẩn 'equity:HOSE:VNM'" },
        title: {
            type: 'string',
            maxLength: 200,
            description: 'Tiêu đề ngắn gọn hồ sơ (≤200 ký tự). VD: "Vinamilk — Đầu ngành sữa, cổ tức đều".',
        },
        summary: {
            type: 'string',
            description: '1 câu tóm tắt hồ sơ (subtitle dưới title).',
        },
        content: {
            type: 'string',
            description: 'Hồ sơ tổng quan markdown: ngành, sản phẩm, cổ đông, chiến lược.',
        },
        tags: {
            type: 'array',
            items: { type: 'string' },
            description: "Nhãn phân loại (VD: ['bluechip', 'consumer'])",
        },
        sectors: {
            type: 'array',
            items: { type: 'string' },
            description: "Ngành (VD: ['consumer_staples'])",
        },
        watchlist_priority: {
            type: 'integer',
            minimum: 0,
            maximum: 100,
            description: 'Độ ưu tiên theo dõi (0-100)',
        },
    },
}, null, 2);

const ajv = new Ajv2020({ strict: false, allErrors: false });

function validateJsonSchema(raw) {
    let schema;
    try {
        schema = JSON.parse(raw);
    } catch (e) {
        return `Invalid JSON: ${e.message}`;
    }
    try {
        ajv.compile(schema);
    } catch (e) {
        return `Invalid JSON Schema: ${e.message}`;
    }
    if (schema.type !== 'object') {
        return 'Root "type" must be "object"';
    }
    if (!schema.properties || Object.keys(schema.properties).length === 0) {
        return '"properties" must have at least one field';
    }
    return null;
}

function formToPayload(form) {
    const executionConfig = { ...(form.execution_config || {}) };
    if (form.session_mode && form.session_mode !== 'isolated') {
        executionConfig.session_mode = form.session_mode;
    } else {
        delete executionConfig.session_mode;
    }
    const timeout = Number(form.timeout_seconds);
    if (Number.isFinite(timeout) && timeout > 0) {
        executionConfig.timeout_seconds = timeout;
    } else {
        delete executionConfig.timeout_seconds;
    }
    const maxRetries = Number(form.max_retries);
    if (Number.isFinite(maxRetries) && maxRetries >= 1) {
        executionConfig.max_retries = maxRetries;
    } else {
        delete executionConfig.max_retries;
    }
    const retryDelay = Number(form.retry_delay_seconds);
    if (Number.isFinite(retryDelay) && retryDelay >= 0) {
        executionConfig.retry_delay_seconds = retryDelay;
    } else {
        delete executionConfig.retry_delay_seconds;
    }
    if (form.structured_output_enabled) {
        const error = validateJsonSchema(form.output_schema_json);
        if (error) throw new Error(error);
        executionConfig.output_schema = JSON.parse(form.output_schema_json);
    } else {
        delete executionConfig.output_schema;
    }

    return {
        id: form.id || undefined,
        name: form.name,
        description: form.description || undefined,
        agent_id: form.agent_id,
        input_message: form.input_message,
        system_prompt_override: form.system_prompt_override ?? '',
        schedule_type: form.schedule_type,
        schedule_time: form.schedule_type === 'trigger' ? undefined : form.schedule_time,
        cron_expression: form.schedule_type === 'cron' ? form.cron_expression : undefined,
        day_of_week: form.schedule_type === 'weekly' ? Number(form.day_of_week) : undefined,
        day_of_month: form.schedule_type === 'monthly' ? Number(form.day_of_month) : undefined,
        timezone: form.timezone,
        target_user_id: form.target_user_id,
        outputs: (form.outputs || []).map((o) => ({
            type: o.type,
            config: o.config || {},
        })),
        default_variables: _parseVariablesJson(form.default_variables_json),
        execution_config: executionConfig,
        attach_full_session: !!form.attach_full_session,
        is_active: form.is_active,
    };
}

function _parseVariablesJson(raw) {
    if (!raw || !raw.trim()) return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
}

const RESERVED_TEMPLATE_ROOTS = new Set([
    'task', 'run', 'now', 'user', 'structured', 'text', 'var',
]);

const PLACEHOLDER_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;

function extractTemplateVariables(template) {
    if (!template || !template.includes('{{')) return [];
    const keys = new Set();
    for (const match of template.matchAll(PLACEHOLDER_RE)) {
        const expr = match[1].trim();
        if (expr.startsWith('#') || expr.startsWith('/')) continue;
        const alternatives = expr.split('|').map((s) => s.trim()).filter(Boolean);
        if (alternatives.length === 0) continue;
        const hasLiteralFallback = alternatives.some((a) => /^["'].*["']$/.test(a));
        if (hasLiteralFallback) continue;
        const firstAlt = alternatives[0];
        const path = firstAlt.split('.').map((s) => s.trim()).filter(Boolean);
        if (path.length === 0) continue;
        let varName;
        if (path[0] === 'var') {
            if (path.length !== 2) continue;
            varName = path[1];
        } else {
            if (RESERVED_TEMPLATE_ROOTS.has(path[0])) continue;
            if (path.length !== 1) continue;
            varName = path[0];
        }
        if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(varName)) continue;
        keys.add(varName);
    }
    return [...keys];
}

const PLACEHOLDER_ROOT_SKIP = new Set(['task', 'run', 'now', 'user']);

const SAMPLE_FIELD_DEFAULTS = {
    'structured.canonical_id': 'equity:HOSE:VNM',
    'structured.ticker': 'VNM',
    'structured.severity': 'high',
    'structured.cta': 'Mua vào',
    'structured.content': 'Nội dung báo cáo mẫu...',
    'structured.summary': 'Tóm tắt ngắn gọn',
    'structured.headline': 'Headline mẫu',
    'structured.title': 'Tiêu đề mẫu',
    'structured.tickers': ['VNM', 'MSN'],
    'var.symbol': 'VNM',
    'var.canonical_id': 'equity:HOSE:VNM',
    'config.category': 'analysis_report',
    today: '2026-04-22',
};

function extractOutputPlaceholderPaths(outputs) {
    const paths = new Set();
    const walk = (v) => {
        if (!v) return;
        if (typeof v === 'string') {
            for (const m of v.matchAll(PLACEHOLDER_RE)) {
                const expr = m[1].trim();
                if (expr.startsWith('#') || expr.startsWith('/')) continue;
                const alts = expr.split('|').map((s) => s.trim()).filter(Boolean);
                for (const alt of alts) {
                    if (/^["'].*["']$/.test(alt)) continue;
                    paths.add(alt);
                }
            }
            return;
        }
        if (Array.isArray(v)) { v.forEach(walk); return; }
        if (typeof v === 'object') { Object.values(v).forEach(walk); }
    };
    for (const o of outputs || []) walk(o?.config);
    return [...paths];
}

function setDeep(obj, pathParts, value) {
    let cur = obj;
    for (let i = 0; i < pathParts.length - 1; i++) {
        const key = pathParts[i];
        if (typeof cur[key] !== 'object' || cur[key] === null || Array.isArray(cur[key])) {
            cur[key] = {};
        }
        cur = cur[key];
    }
    const leaf = pathParts[pathParts.length - 1];
    if (!(leaf in cur)) cur[leaf] = value;
}

function sampleValueFor(path) {
    if (path in SAMPLE_FIELD_DEFAULTS) return SAMPLE_FIELD_DEFAULTS[path];
    return '';
}

function buildSampleContext(outputs, defaultVariables) {
    const ctx = {
        structured: { canonical_id: 'equity:HOSE:VNM' },
        var: { ...(defaultVariables || {}) },
        text: 'Tóm tắt mẫu...',
        task: { id: 'preview', name: 'Tác vụ mẫu', agent_id: 'simplize' },
        run: { id: 0 },
    };
    for (const path of extractOutputPlaceholderPaths(outputs)) {
        const parts = path.split('.').map((s) => s.trim()).filter(Boolean);
        if (parts.length === 0) continue;
        if (PLACEHOLDER_ROOT_SKIP.has(parts[0])) continue;
        if (parts.length === 1) {
            const key = parts[0];
            if (key === 'var' || key === 'structured' || key === 'text') continue;
            if (!(key in ctx)) ctx[key] = sampleValueFor(key);
            continue;
        }
        setDeep(ctx, parts, sampleValueFor(path));
    }
    return ctx;
}

function configToForm(task) {
    const c = task.config || {};
    const s = c.schedule || {};
    return {
        id: task.id,
        name: task.name || '',
        description: task.description || '',
        agent_id: task.agent_id || c.agent_id || 'simplize',
        input_message: c.input_message || '',
        system_prompt_override: c.system_prompt_override || '',
        schedule_type: s.type || 'daily',
        schedule_time: s.time || '07:00',
        cron_expression: s.cron_expression || '',
        day_of_week: s.day_of_week ?? 1,
        day_of_month: s.day_of_month ?? 1,
        timezone: s.timezone || 'Asia/Ho_Chi_Minh',
        target_user_id: task.target_user_id || c.target_user_id || '',
        outputs: Array.isArray(c.outputs) ? JSON.parse(JSON.stringify(c.outputs)) : [],
        default_variables_json: c.default_variables
            ? JSON.stringify(c.default_variables, null, 2)
            : '{}',
        execution_config: JSON.parse(JSON.stringify(c.execution || {})),
        session_mode: c.execution?.session_mode || 'isolated',
        timeout_seconds: c.execution?.timeout_seconds ?? 300,
        max_retries: c.execution?.max_retries ?? 1,
        retry_delay_seconds: c.execution?.retry_delay_seconds ?? 30,
        structured_output_enabled: !!(c.execution?.output_schema),
        output_schema_json: c.execution?.output_schema
            ? JSON.stringify(c.execution.output_schema, null, 2)
            : '',
        attach_full_session: c.attach_full_session !== false,
        is_active: task.is_active ?? true,
    };
}

const TYPE_LABEL_VI = {
    string: 'văn bản',
    number: 'số',
    integer: 'số nguyên',
    boolean: 'đúng/sai',
    object: 'nhóm',
    array: 'danh sách',
    null: 'trống',
};

function SchemaPropertyNode({ name, prop, required, depth = 0 }) {
    const childProps = prop.properties
        ? { entries: Object.entries(prop.properties), required: prop.required || [] }
        : prop.items?.properties
            ? { entries: Object.entries(prop.items.properties), required: prop.items.required || [], itemLabel: true }
            : null;

    const type = prop.type || 'any';
    const typeLabel = TYPE_LABEL_VI[type] || type;

    return (
        <div className="border-l border-gray-100 pl-2" style={{ marginLeft: depth * 10 }}>
            <div className="flex items-baseline gap-1.5 flex-wrap py-0.5">
                <span className="font-mono text-xs font-medium text-gray-800">{name}</span>
                <span className="text-[10px] text-gray-500">{typeLabel}</span>
                {required && <span className="text-[10px] text-red-500">· bắt buộc</span>}
                {prop.enum && <span className="text-[10px] text-amber-600">· {prop.enum.join(' | ')}</span>}
            </div>
            {prop.description && <p className="text-[11px] text-gray-500 leading-snug pb-0.5">{prop.description}</p>}
            {childProps && (
                <div>
                    {childProps.itemLabel && (
                        <span className="text-[9px] text-gray-400 uppercase tracking-wide ml-1">phần tử:</span>
                    )}
                    {childProps.entries.map(([k, v]) => (
                        <SchemaPropertyNode
                            key={k}
                            name={k}
                            prop={v}
                            required={childProps.required.includes(k)}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function SchemaPreview({ schemaJson, showHeader = true, emptyHint = 'Chưa có thuộc tính nào.' }) {
    let schema;
    try {
        schema = JSON.parse(schemaJson || '{}');
    } catch (e) {
        return <div className="text-sm text-red-500">JSON không hợp lệ: {e.message}</div>;
    }
    const props = schema.properties || {};
    const reqFields = schema.required || [];
    if (Object.keys(props).length === 0) {
        return <p className="text-sm text-gray-400 italic">{emptyHint}</p>;
    }
    return (
        <div className="space-y-1">
            {showHeader && schema.title && <h4 className="text-base font-semibold text-gray-800 mb-2">{schema.title}</h4>}
            {showHeader && schema.description && <p className="text-sm text-gray-500 mb-3">{schema.description}</p>}
            {Object.entries(props).map(([k, v]) => (
                <SchemaPropertyNode key={k} name={k} prop={v} required={reqFields.includes(k)} />
            ))}
        </div>
    );
}

export default function AdminScheduledTaskForm() {
    const { taskId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const isCreate = !taskId;
    const cloneFrom = isCreate ? searchParams.get('clone') : null;

    const [form, setForm] = useState(DEFAULT_FORM);
    const [showCronHelp, setShowCronHelp] = useState(false);
    const [showSchemaEditor, setShowSchemaEditor] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [editorMode, setEditorMode] = useState('json');
    const [loading, setLoading] = useState(!isCreate || !!cloneFrom);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [agents, setAgents] = useState([]);
    const [schemaTemplateConfirm, setSchemaTemplateConfirm] = useState(null);
    const inputMsgRef = useRef(null);
    const sysMsgRef = useRef(null);

    const autoResizeEl = useCallback((el) => {
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 500) + 'px';
    }, []);

    useEffect(() => {
        if (!loading) {
            setTimeout(() => {
                autoResizeEl(inputMsgRef.current);
                autoResizeEl(sysMsgRef.current);
            }, 50);
        }
    }, [loading, autoResizeEl]);
    useEffect(() => {
        loadAgents();
        if (cloneFrom) {
            loadClone(cloneFrom);
        } else if (!isCreate) {
            loadTask();
        }
    }, [taskId, cloneFrom]);

    const loadAgents = async () => {
        try {
            const data = await adminApi.listAgents({});
            setAgents((data?.agents || []).map(a => ({ value: a.id, label: `${a.name} (${a.id})` })));
        } catch { /* ignore */ }
    };

    const loadClone = async (sourceId) => {
        try {
            setLoading(true);
            const task = await adminApi.getScheduledTask(sourceId);
            const cloned = configToForm(task);
            cloned.id = crypto.randomUUID().slice(0, 12);
            cloned.name = `${cloned.name} (Bản sao)`;
            cloned.is_active = false;
            setForm(cloned);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadTask = async () => {
        try {
            setLoading(true);
            const task = await adminApi.getScheduledTask(taskId);
            setForm(configToForm(task));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
    };

    const applySchemaTemplate = (template, label) => {
        const current = (form.output_schema_json || '').trim();
        if (current && current !== template.trim()) {
            setSchemaTemplateConfirm({ template, label });
            return;
        }
        handleChange('output_schema_json', template);
    };

    const confirmApplyTemplate = () => {
        if (schemaTemplateConfirm) {
            handleChange('output_schema_json', schemaTemplateConfirm.template);
        }
        setSchemaTemplateConfirm(null);
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) { setError('Vui lòng nhập tên tác vụ'); return; }
        if (!form.input_message.trim()) { setError('Vui lòng nhập tin nhắn đầu vào'); return; }
        if (!form.target_user_id.trim()) { setError('Vui lòng nhập mã người dùng nhận kết quả'); return; }
        if (form.schedule_type === 'cron' && !form.cron_expression.trim()) {
            setError('Biểu thức cron là bắt buộc khi chọn loại lịch "cron"');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const payload = formToPayload(form);
            if (isCreate) {
                await adminApi.createScheduledTask(payload);
            } else {
                await adminApi.updateScheduledTask(taskId, payload);
            }
            navigate('/admin/scheduled-tasks');
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Spinner size="lg" text="Đang tải..." center />;

    return (
        <div className="w-full">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-lg font-semibold text-gray-900">
                    {isCreate ? 'Tạo tác vụ lập lịch' : `Chỉnh sửa — ${form.name}`}
                </h1>
                <Button variant="ghost" onClick={() => navigate('/admin/scheduled-tasks')}>
                    Quay lại
                </Button>
            </div>

            {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}

            <div className="space-y-6">

                {/* Thông tin chung */}
                <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-4 shadow-sm">
                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Thông tin chung</h2>
                    {isCreate ? (
                        <Input
                            label="Mã tác vụ"
                            hint="Tuỳ chọn. Để trống sẽ tự sinh."
                            value={form.id}
                            onChange={(e) => handleChange('id', e.target.value)}
                            placeholder="morning-report-vn"
                        />
                    ) : (
                        <Input
                            label="Mã tác vụ"
                            hint="Không thể chỉnh sửa sau khi đã tạo."
                            value={form.id}
                            readOnly
                            disabled
                            className="font-mono"
                        />
                    )}
                    <Input
                        label="Tên tác vụ"
                        value={form.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Bản tin thị trường buổi sáng"
                    />
                    <Input
                        label="Mô tả"
                        value={form.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        placeholder="Bản tin TTCK Việt Nam mỗi sáng 7h T2-T6"
                    />
                    <div className="flex items-center gap-3">
                        <Toggle
                            checked={form.is_active}
                            onChange={(val) => handleChange('is_active', val)}
                        />
                        <span className="text-sm text-gray-600">Kích hoạt</span>
                    </div>
                    <div className="flex items-start gap-3 pt-2 border-t border-gray-100">
                        <Toggle
                            checked={form.attach_full_session}
                            onChange={(val) => handleChange('attach_full_session', val)}
                        />
                        <div className="flex-1">
                            <div className="text-sm text-gray-700 font-medium">Đính kèm toàn bộ phiên làm việc</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                                Sau khi task chạy xong, xuất transcript đầy đủ (prompt → tool call → response → reply) ra S3
                                và gắn vào insight. Chat follow-up sẽ có ngữ cảnh + nguồn dẫn chứng, không chỉ bản báo cáo cuối.
                            </div>
                        </div>
                    </div>
                </section>

                {/* Agent */}
                <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-4 shadow-sm">
                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Trợ lý</h2>
                    <Select
                        label="Trợ lý (Agent)"
                        value={form.agent_id}
                        onChange={(e) => handleChange('agent_id', e.target.value)}
                        options={agents.length > 0 ? agents : [{ value: form.agent_id, label: form.agent_id }]}
                    />
                    <Input
                        label="Mã người dùng nhận kết quả"
                        hint="Người dùng sẽ nhận kết quả trên ứng dụng"
                        value={form.target_user_id}
                        onChange={(e) => handleChange('target_user_id', e.target.value)}
                        placeholder="user-id"
                    />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tin nhắn đầu vào</label>
                        <p className="text-xs text-gray-500 mb-1">Prompt gửi cho trợ lý mỗi lần chạy</p>
                        <textarea
                            ref={inputMsgRef}
                            value={form.input_message}
                            onChange={(e) => { handleChange('input_message', e.target.value); autoResizeEl(e.target); }}
                            rows={4}
                            placeholder="Tạo báo cáo buổi sáng về thị trường chứng khoán VN..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                            style={{ minHeight: '120px', maxHeight: '500px', overflow: 'auto', resize: 'none' }}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">System prompt bổ sung</label>
                        <p className="text-xs text-gray-500 mb-1">Tuỳ chọn. Thêm chỉ dẫn giọng văn / phong cách riêng cho tác vụ.</p>
                        <textarea
                            ref={sysMsgRef}
                            value={form.system_prompt_override}
                            onChange={(e) => { handleChange('system_prompt_override', e.target.value); autoResizeEl(e.target); }}
                            rows={3}
                            placeholder="Bạn là chuyên gia phân tích thị trường..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                            style={{ minHeight: '120px', maxHeight: '500px', overflow: 'auto', resize: 'none' }}
                        />
                    </div>
                </section>

                {/* Thực thi */}
                <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-4 shadow-sm">
                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Thực thi</h2>
                    <Select
                        label="Chế độ phiên"
                        value={form.session_mode}
                        onChange={(e) => handleChange('session_mode', e.target.value)}
                        options={[
                            { value: 'isolated', label: 'Độc lập — Mỗi lần chạy là phiên riêng' },
                            { value: 'continuous', label: 'Liên tục — Nối tiếp phiên trước (có ngữ cảnh)' },
                        ]}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <Input
                                label="Timeout (giây)"
                                hint="default 300"
                                type="number"
                                min={30}
                                max={3600}
                                value={form.timeout_seconds}
                                onChange={(e) => handleChange('timeout_seconds', e.target.value ? parseInt(e.target.value, 10) : '')}
                            />
                            <p className="text-xs text-gray-500 mt-1">Skill agent gọi nhiều tool nên set 600-900s.</p>
                        </div>
                        <div>
                            <Input
                                label="Max retries"
                                hint="default 1"
                                type="number"
                                min={1}
                                max={5}
                                value={form.max_retries}
                                onChange={(e) => handleChange('max_retries', e.target.value ? parseInt(e.target.value, 10) : '')}
                            />
                            <p className="text-xs text-gray-500 mt-1">Số lần thử tối đa (1 = không retry).</p>
                        </div>
                        <div>
                            <Input
                                label="Retry delay (giây)"
                                hint="default 30"
                                type="number"
                                min={0}
                                max={600}
                                value={form.retry_delay_seconds}
                                onChange={(e) => handleChange('retry_delay_seconds', e.target.value ? parseInt(e.target.value, 10) : '')}
                            />
                            <p className="text-xs text-gray-500 mt-1">Chờ giữa 2 lần retry.</p>
                        </div>
                    </div>
                </section>

                {/* Kết quả có cấu trúc */}
                <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-4 shadow-sm">
                    <div className="flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Kết quả có cấu trúc</h2>
                        <div className="flex items-center gap-2">
                            <Toggle
                                checked={form.structured_output_enabled}
                                onChange={(val) => {
                                    handleChange('structured_output_enabled', val);
                                    if (val && !form.output_schema_json) {
                                        handleChange('output_schema_json', DEFAULT_SCHEMA_EXAMPLE);
                                    }
                                }}
                            />
                            <span className="text-sm text-gray-600">Bật</span>
                        </div>
                    </div>
                    {form.structured_output_enabled && (
                        <>
                            {form.output_schema_json && (() => {
                                const err = validateJsonSchema(form.output_schema_json);
                                let summary;
                                try {
                                    const parsed = JSON.parse(form.output_schema_json);
                                    const fieldCount = Object.keys(parsed.properties || {}).length;
                                    const required = (parsed.required || []).length;
                                    summary = err ? (
                                        <span className="text-red-700 text-xs">{err}</span>
                                    ) : (
                                        <span className="text-green-700 text-xs">
                                            Schema hợp lệ — {fieldCount} trường, {required} bắt buộc
                                            {parsed.title && <span className="text-green-600 ml-1">({parsed.title})</span>}
                                        </span>
                                    );
                                } catch {
                                    summary = <span className="text-red-700 text-xs">{err || 'JSON không hợp lệ'}</span>;
                                }
                                const tone = err ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200';
                                return (
                                    <div className={`px-4 py-3 rounded-lg border text-sm space-y-3 ${tone}`}>
                                        <div>{summary}</div>
                                        {!err && (
                                            <details className="group" open>
                                                <summary className="cursor-pointer text-xs font-medium text-gray-600 hover:text-gray-800 select-none flex items-center gap-1">
                                                    <svg className="h-3 w-3 transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                                    </svg>
                                                    Xem cấu trúc schema
                                                </summary>
                                                <div className="mt-2 p-2 bg-white border border-gray-200 rounded-md max-h-96 overflow-auto">
                                                    <SchemaPreview schemaJson={form.output_schema_json} />
                                                </div>
                                            </details>
                                        )}
                                        <Button variant="outline" size="xs" onClick={() => setShowSchemaEditor(true)}>
                                            Chỉnh sửa schema
                                        </Button>
                                    </div>
                                );
                            })()}
                            {!form.output_schema_json && (
                                <Button variant="secondary" size="sm" onClick={() => setShowSchemaEditor(true)}>
                                    Thiết lập JSON schema
                                </Button>
                            )}
                        </>
                    )}
                </section>

                {/* Schema Editor Popup — Full width, side-by-side */}
                {showSchemaEditor && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-stretch p-4" onClick={() => setShowSchemaEditor(false)}>
                        <div className="bg-white rounded-xl shadow-2xl w-full flex flex-col" onClick={e => e.stopPropagation()}>
                            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between shrink-0">
                                <h3 className="text-lg font-semibold text-gray-800">Trình chỉnh sửa JSON schema</h3>
                                <div className="flex items-center gap-2">
                                    <Button variant="ghost" size="xs" onClick={() => applySchemaTemplate(INSIGHT_ANALYSIS_SCHEMA, 'Mẫu Insight')}>Mẫu Insight</Button>
                                    <Button variant="ghost" size="xs" onClick={() => applySchemaTemplate(INSTRUMENT_PROFILE_SCHEMA, 'Mẫu Profile')}>Mẫu Profile</Button>
                                    <Button variant="ghost" size="xs" onClick={() => {
                                        try { handleChange('output_schema_json', JSON.stringify(JSON.parse(form.output_schema_json), null, 2)); }
                                        catch (e) { setError('JSON không hợp lệ: ' + e.message); }
                                    }}>Định dạng</Button>
                                    <button onClick={() => setShowSchemaEditor(false)} className="ml-2 text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                                </div>
                            </div>
                            <div className="flex-1 flex overflow-hidden">
                                {/* Left — Editor panel with mode switcher */}
                                <div className="w-1/2 border-r border-gray-100 flex flex-col">
                                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between shrink-0">
                                        <div className="flex items-center gap-2">
                                            <div className="flex bg-gray-200 rounded-md p-0.5">
                                                {[
                                                    { key: 'json', label: 'JSON' },
                                                    { key: 'tree', label: 'Cây' },
                                                    { key: 'text', label: 'Văn bản' },
                                                ].map(m => (
                                                    <button
                                                        key={m.key}
                                                        className={`px-2.5 py-1 text-[11px] font-medium rounded transition-colors ${editorMode === m.key ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                                                        onClick={() => setEditorMode(m.key)}
                                                    >
                                                        {m.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        {(() => {
                                            if (!form.output_schema_json) return null;
                                            const err = validateJsonSchema(form.output_schema_json);
                                            return err
                                                ? <span className="text-xs text-red-500 truncate max-w-[250px]">{err}</span>
                                                : <span className="text-xs text-green-600">Schema hợp lệ</span>;
                                        })()}
                                    </div>
                                    <div className="flex-1 overflow-hidden">
                                        {editorMode === 'json' && (
                                            <Editor
                                                height="100%"
                                                language="json"
                                                theme="vs"
                                                value={form.output_schema_json}
                                                onChange={(value) => handleChange('output_schema_json', value || '')}
                                                options={{
                                                    minimap: { enabled: false },
                                                    fontSize: 13,
                                                    lineNumbers: 'on',
                                                    scrollBeyondLastLine: false,
                                                    wordWrap: 'on',
                                                    formatOnPaste: true,
                                                    automaticLayout: true,
                                                    tabSize: 2,
                                                    folding: true,
                                                    bracketPairColorization: { enabled: true },
                                                }}
                                            />
                                        )}
                                        {editorMode === 'tree' && (
                                            <div className="h-full overflow-auto p-4">
                                                {(() => {
                                                    try {
                                                        const parsed = JSON.parse(form.output_schema_json || '{}');
                                                        return (
                                                            <JsonView
                                                                src={parsed}
                                                                editable
                                                                enableClipboard
                                                                collapsed={2}
                                                                theme="default"
                                                                style={{ fontSize: '13px', lineHeight: '1.6' }}
                                                                onEdit={(params) => handleChange('output_schema_json', JSON.stringify(params.src, null, 2))}
                                                                onAdd={(params) => handleChange('output_schema_json', JSON.stringify(params.src, null, 2))}
                                                                onDelete={(params) => handleChange('output_schema_json', JSON.stringify(params.src, null, 2))}
                                                            />
                                                        );
                                                    } catch {
                                                        return (
                                                            <div className="p-4 text-sm text-red-500">
                                                                JSON không hợp lệ. Chuyển sang chế độ <strong>JSON</strong> hoặc <strong>Văn bản</strong> để sửa.
                                                            </div>
                                                        );
                                                    }
                                                })()}
                                            </div>
                                        )}
                                        {editorMode === 'text' && (
                                            <textarea
                                                value={form.output_schema_json}
                                                onChange={(e) => handleChange('output_schema_json', e.target.value)}
                                                className="w-full h-full font-mono text-xs p-4 resize-none focus:outline-none bg-white"
                                                placeholder="Dán văn bản JSON thô ở đây..."
                                                spellCheck={false}
                                            />
                                        )}
                                    </div>
                                </div>
                                {/* Right — Schema Preview */}
                                <div className="w-1/2 flex flex-col">
                                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 shrink-0">
                                        <span className="text-xs font-medium text-gray-600 uppercase">Xem trước schema</span>
                                    </div>
                                    <div className="flex-1 overflow-auto p-4">
                                        <SchemaPreview
                                            schemaJson={form.output_schema_json}
                                            emptyHint="Chưa có thuộc tính nào. Thêm trường ở khung bên trái."
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2 shrink-0">
                                <Button variant="secondary" onClick={() => setShowSchemaEditor(false)}>
                                    Huỷ
                                </Button>
                                <Button
                                    onClick={() => {
                                        // Force any in-progress input (Monaco / textarea / tree
                                        // value cell) to commit its pending value before we read
                                        // the form. Without this, fast clicks after a keystroke
                                        // could close the popup with the latest character lost.
                                        try { document.activeElement?.blur?.(); } catch { /* ignore */ }
                                        // Defer one tick so React flushes the onChange triggered
                                        // by the blur above before we validate / format.
                                        setTimeout(() => {
                                            const err = validateJsonSchema(form.output_schema_json);
                                            if (err) {
                                                setError(`JSON schema không hợp lệ: ${err}`);
                                                return;
                                            }
                                            try {
                                                const formatted = JSON.stringify(
                                                    JSON.parse(form.output_schema_json),
                                                    null,
                                                    2,
                                                );
                                                handleChange('output_schema_json', formatted);
                                            } catch { /* validateJsonSchema already caught it */ }
                                            setError(null);
                                            setShowSchemaEditor(false);
                                        }, 0);
                                    }}
                                >
                                    Lưu schema
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Lịch chạy */}
                <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-4 shadow-sm">
                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Lịch chạy</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <Select
                            label="Loại lịch"
                            value={form.schedule_type}
                            onChange={(e) => handleChange('schedule_type', e.target.value)}
                            options={SCHEDULE_TYPES}
                        />
                        {form.schedule_type !== 'cron' && form.schedule_type !== 'trigger' && (
                            <Input
                                label="Giờ chạy"
                                type="time"
                                value={form.schedule_time}
                                onChange={(e) => handleChange('schedule_time', e.target.value)}
                            />
                        )}
                    </div>
                    {form.schedule_type === 'trigger' && (
                        <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                            Chỉ chạy thủ công — tác vụ sẽ không tự động kích hoạt. Dùng nút <strong>Chạy ngay</strong> hoặc API <code>POST /run</code> để kích hoạt.
                        </div>
                    )}

                    {form.schedule_type === 'weekly' && (
                        <Select
                            label="Ngày trong tuần"
                            value={form.day_of_week}
                            onChange={(e) => handleChange('day_of_week', e.target.value)}
                            options={DAYS_OF_WEEK}
                        />
                    )}
                    {form.schedule_type === 'monthly' && (
                        <Input
                            label="Ngày trong tháng"
                            type="number"
                            min={1} max={31}
                            value={form.day_of_month}
                            onChange={(e) => handleChange('day_of_month', e.target.value)}
                        />
                    )}
                    {form.schedule_type === 'cron' && (
                        <div className="space-y-2">
                            <div className="flex items-end gap-2">
                                <div className="flex-1">
                                    <Input
                                        label="Biểu thức Cron"
                                        value={form.cron_expression}
                                        onChange={(e) => handleChange('cron_expression', e.target.value)}
                                        placeholder="0 7 * * 1-5"
                                        hint="5 trường: phút giờ ngày tháng thứ"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowCronHelp(true)}
                                    className="mb-1 px-3 py-2 text-xs bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors whitespace-nowrap"
                                >
                                    Ví dụ
                                </button>
                            </div>
                            {showCronHelp && (
                                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowCronHelp(false)}>
                                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
                                        <div className="p-5 border-b border-gray-100">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-lg font-semibold text-gray-800">Hướng dẫn biểu thức Cron</h3>
                                                <button onClick={() => setShowCronHelp(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">Nhấp vào ví dụ để sử dụng</p>
                                        </div>
                                        <div className="p-5 space-y-4">
                                            <div className="bg-indigo-50 rounded-lg p-4">
                                                <div className="text-xs font-mono text-gray-600 mb-2 flex justify-between">
                                                    <span>*</span><span>*</span><span>*</span><span>*</span><span>*</span>
                                                </div>
                                                <div className="text-[10px] text-gray-500 flex justify-between">
                                                    <span>phút<br/>0-59</span>
                                                    <span>giờ<br/>0-23</span>
                                                    <span>ngày<br/>1-31</span>
                                                    <span>tháng<br/>1-12</span>
                                                    <span>thứ<br/>0-6</span>
                                                </div>
                                            </div>
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="text-left text-gray-500 text-xs uppercase">
                                                        <th className="pb-2 pr-4">Biểu thức</th>
                                                        <th className="pb-2">Ý nghĩa</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {[
                                                        ['0 7 * * 1-5', '7:00 sáng T2-T6'],
                                                        ['0 7 * * *', '7:00 sáng mỗi ngày'],
                                                        ['30 8 * * 1-5', '8:30 sáng T2-T6'],
                                                        ['0 17 * * 1-5', '17:00 chiều T2-T6'],
                                                        ['0 9,12,17 * * 1-5', '9h, 12h, 17h T2-T6'],
                                                        ['*/15 * * * *', 'Mỗi 15 phút'],
                                                        ['*/30 9-17 * * 1-5', 'Mỗi 30 phút (9h-17h, T2-T6)'],
                                                        ['0 0 * * *', '0:00 mỗi đêm'],
                                                        ['0 15 * * 5', '15:00 chiều thứ 6'],
                                                        ['0 8 1 * *', '8:00 ngày 1 hàng tháng'],
                                                        ['0 8 1,15 * *', '8:00 ngày 1 & 15 hàng tháng'],
                                                        ['0 0 1 1 0', 'Không bao giờ (dùng cho tác vụ tắt)'],
                                                    ].map(([expr, desc]) => (
                                                        <tr
                                                            key={expr}
                                                            className="cursor-pointer hover:bg-indigo-50 transition-colors"
                                                            onClick={() => { handleChange('cron_expression', expr); setShowCronHelp(false); }}
                                                        >
                                                            <td className="py-2.5 pr-4 font-mono text-indigo-700 text-xs">{expr}</td>
                                                            <td className="py-2.5 text-gray-700">{desc}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            <div className="text-xs text-gray-400 border-t pt-3 space-y-1">
                                                <p><strong>Ký tự đặc biệt:</strong></p>
                                                <p><code className="bg-gray-100 px-1 rounded">*</code> = bất kỳ &nbsp; <code className="bg-gray-100 px-1 rounded">*/N</code> = mỗi N &nbsp; <code className="bg-gray-100 px-1 rounded">1-5</code> = khoảng &nbsp; <code className="bg-gray-100 px-1 rounded">1,3,5</code> = danh sách</p>
                                                <p><strong>Thứ:</strong> 0=CN, 1=T2, 2=T3, 3=T4, 4=T5, 5=T6, 6=T7</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <Select
                        label="Múi giờ"
                        value={form.timezone}
                        onChange={(e) => handleChange('timezone', e.target.value)}
                        options={TIMEZONES}
                    />
                </section>

                <DefaultVariablesSection
                    inputMessage={form.input_message}
                    variablesJson={form.default_variables_json}
                    onChange={(value) => handleChange('default_variables_json', value)}
                />


                {/* Đầu ra */}
                <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Đầu ra</h2>
                            <p className="text-xs text-gray-500 mt-1">
                                Cấu hình các kênh nhận kết quả sau khi tác vụ chạy xong. Mỗi đầu ra có schema riêng do server cung cấp, giao diện tự render.
                            </p>
                        </div>
                        <Button
                            variant="secondary" size="sm"
                            onClick={() => setShowPreview(true)}
                            disabled={!(form.outputs || []).length}
                        >
                            Xem trước biến
                        </Button>
                    </div>
                    <TaskOutputsEditor
                        value={form.outputs}
                        onChange={(next) => handleChange('outputs', next)}
                    />
                </section>

                {showPreview && (
                    <PreviewModal
                        outputs={form.outputs}
                        defaultVariables={_parseVariablesJson(form.default_variables_json)}
                        onClose={() => setShowPreview(false)}
                    />
                )}

                <Modal
                    open={!!schemaTemplateConfirm}
                    onClose={() => setSchemaTemplateConfirm(null)}
                    title="Thay thế schema hiện tại?"
                    size="sm"
                >
                    <Modal.Body>
                        <p className="text-sm text-gray-700">
                            Schema hiện tại sẽ bị ghi đè bằng{' '}
                            <span className="font-semibold text-gray-900">
                                {schemaTemplateConfirm?.label}
                            </span>
                            . Hành động này không thể hoàn tác.
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" size="sm" onClick={() => setSchemaTemplateConfirm(null)}>
                            Huỷ
                        </Button>
                        <Button variant="primary" size="sm" onClick={confirmApplyTemplate}>
                            Thay thế
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                    <Button variant="secondary" onClick={() => navigate('/admin/scheduled-tasks')}>
                        Huỷ
                    </Button>
                    <Button onClick={handleSubmit} loading={saving}>
                        {isCreate ? 'Tạo tác vụ' : 'Lưu thay đổi'}
                    </Button>
                </div>
            </div>
        </div>
    );
}


function DefaultVariablesSection({ inputMessage, variablesJson, onChange }) {
    const suggestedKeys = useMemo(() => extractTemplateVariables(inputMessage), [inputMessage]);

    const parsed = useMemo(() => {
        const raw = variablesJson || '';
        if (!raw.trim()) return { ok: true, value: {} };
        try {
            const v = JSON.parse(raw);
            if (v && typeof v === 'object' && !Array.isArray(v)) return { ok: true, value: v };
            return { ok: false, error: 'Phải là object JSON (khoá-giá trị)' };
        } catch (e) {
            return { ok: false, error: `JSON không hợp lệ: ${e.message}` };
        }
    }, [variablesJson]);

    const existingKeys = parsed.ok ? Object.keys(parsed.value) : [];
    const missingKeys = suggestedKeys.filter((k) => !existingKeys.includes(k));

    const fillFromTemplate = useCallback(() => {
        if (!parsed.ok || suggestedKeys.length === 0) return;
        const merged = { ...Object.fromEntries(suggestedKeys.map((k) => [k, ''])), ...parsed.value };
        onChange(JSON.stringify(merged, null, 2));
    }, [parsed, suggestedKeys, onChange]);

    const addMissingKey = useCallback((key) => {
        const base = parsed.ok ? parsed.value : {};
        onChange(JSON.stringify({ ...base, [key]: '' }, null, 2));
    }, [parsed, onChange]);

    const validMsg = parsed.ok
        ? (Object.keys(parsed.value).length > 0 ? `Hợp lệ — ${Object.keys(parsed.value).length} biến` : null)
        : parsed.error;

    return (
        <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-3 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Biến mặc định</h2>
                    <p className="text-xs text-gray-500 mt-1">
                        Biến dùng khi cron tự kích hoạt và không có tham số runtime. Sẽ bị override bởi <code className="px-1 bg-gray-100 rounded">variables</code> truyền qua API <code>POST /run</code>. Dùng trong input message hoặc output config qua cú pháp <code>{`{{ten_bien}}`}</code> hoặc <code>{`{{var.ten_bien}}`}</code>.
                    </p>
                </div>
                {suggestedKeys.length > 0 && (
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={fillFromTemplate}
                        disabled={!parsed.ok}
                    >
                        Điền mẫu từ template ({suggestedKeys.length})
                    </Button>
                )}
            </div>

            {suggestedKeys.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                    <span className="text-gray-500">Biến phát hiện trong input:</span>
                    {suggestedKeys.map((k) => {
                        const present = existingKeys.includes(k);
                        const hasValue = present && String(parsed.value[k] ?? '').length > 0;
                        const tone = hasValue
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : present
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 cursor-pointer';
                        return (
                            <span
                                key={k}
                                onClick={present ? undefined : () => addMissingKey(k)}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border font-mono text-[11px] ${tone}`}
                                title={present ? (hasValue ? 'Đã có giá trị' : 'Đã khai báo nhưng rỗng') : 'Click để thêm'}
                            >
                                {hasValue ? '✓' : present ? '○' : '+'} {k}
                            </span>
                        );
                    })}
                </div>
            )}

            <textarea
                value={variablesJson}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs focus:outline-none focus:ring-2 focus:ring-indigo-300"
                rows={Math.max(4, (suggestedKeys.length || 0) + 2)}
                placeholder={
                    suggestedKeys.length > 0
                        ? JSON.stringify(Object.fromEntries(suggestedKeys.map((k) => [k, ''])), null, 2)
                        : '{"symbol": "equity:HOSE:VNM", "language": "vi"}'
                }
                spellCheck={false}
            />
            {validMsg && (
                <span className={`text-xs ${parsed.ok ? 'text-green-600' : 'text-red-500'}`}>
                    {validMsg}
                </span>
            )}
            {parsed.ok && missingKeys.length > 0 && (
                <div className="text-xs text-amber-600">
                    Thiếu biến: {missingKeys.join(', ')} — cron sẽ fail nếu placeholder không có fallback <code>{`{{name | "default"}}`}</code>.
                </div>
            )}
        </section>
    );
}


function PreviewModal({ outputs, defaultVariables, onClose }) {
    const [sampleJson, setSampleJson] = useState(() =>
        JSON.stringify(buildSampleContext(outputs, defaultVariables), null, 2)
    );

    const hydrateFromOutputs = useCallback(() => {
        setSampleJson(JSON.stringify(buildSampleContext(outputs, defaultVariables), null, 2));
    }, [outputs, defaultVariables]);
    const [result, setResult] = useState(null);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState(null);

    const runPreview = async () => {
        setError(null);
        setResult(null);
        let sampleContext;
        try {
            sampleContext = JSON.parse(sampleJson);
        } catch (e) {
            setError('JSON ngữ cảnh không hợp lệ: ' + e.message);
            return;
        }
        setRunning(true);
        try {
            const resp = await adminApi.previewScheduledTaskOutputs({
                outputs: (outputs || []).map((o) => ({ type: o.type, config: o.config || {} })),
                sample_context: sampleContext,
            });
            setResult(resp);
        } catch (e) {
            setError(e?.message || 'Không gọi được API preview');
        } finally {
            setRunning(false);
        }
    };

    useEffect(() => { runPreview(); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between shrink-0">
                    <h3 className="text-lg font-semibold text-gray-800">Xem trước biến trong đầu ra</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                </div>
                <div className="flex-1 grid grid-cols-2 divide-x divide-gray-100 overflow-hidden">
                    <div className="flex flex-col">
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-600 uppercase">Ngữ cảnh mẫu</span>
                            <div className="flex items-center gap-2">
                                <Button variant="secondary" size="xs" onClick={hydrateFromOutputs}>
                                    Tự sinh từ outputs
                                </Button>
                                <Button variant="primary" size="xs" onClick={runPreview} loading={running}>
                                    Chạy lại
                                </Button>
                            </div>
                        </div>
                        <textarea
                            className="flex-1 p-4 font-mono text-xs resize-none focus:outline-none"
                            value={sampleJson}
                            onChange={(e) => setSampleJson(e.target.value)}
                            spellCheck={false}
                        />
                    </div>
                    <div className="flex flex-col overflow-hidden">
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                            <span className="text-xs font-medium text-gray-600 uppercase">Kết quả sau resolve</span>
                        </div>
                        <div className="flex-1 overflow-auto p-4 space-y-3">
                            {error && <div className="text-xs text-red-600">{error}</div>}
                            {result?.items?.map((item) => (
                                <div
                                    key={item.index}
                                    className={`border rounded-lg p-3 ${item.ok ? 'border-green-200 bg-green-50/40' : 'border-red-200 bg-red-50/40'}`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-semibold text-gray-800">
                                            #{item.index} · {item.type}
                                        </span>
                                        <span className={`text-xs ${item.ok ? 'text-green-700' : 'text-red-700'}`}>
                                            {item.ok ? '✓ resolve OK' : '✗ lỗi'}
                                        </span>
                                    </div>
                                    {item.placeholders?.length > 0 && (
                                        <div className="mb-2">
                                            <div className="text-[10px] text-gray-400 uppercase mb-0.5">Biến được sử dụng</div>
                                            <div className="flex flex-wrap gap-1">
                                                {item.placeholders.map((p, i) => (
                                                    <code key={i} className="text-[11px] bg-white border border-gray-200 rounded px-1.5 py-0.5 font-mono">{p}</code>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {item.ok ? (
                                        <pre className="text-[11px] whitespace-pre-wrap break-words bg-white p-2 rounded border border-gray-200 max-h-48 overflow-auto">
                                            {JSON.stringify(item.resolved_config, null, 2)}
                                        </pre>
                                    ) : (
                                        <div className="text-xs text-red-700">{item.error}</div>
                                    )}
                                </div>
                            ))}
                            {!running && !result && !error && (
                                <div className="text-xs text-gray-400">Nhấn "Chạy lại" để xem kết quả resolve.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
